/**
 * DemandForm - Sprint 7/8 Demand Management
 * Create/Edit form for demands with AI-powered templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Save, AlertCircle, Sparkles, Server, Code, 
  Workflow, Shield, Users, RefreshCw, X 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template icons mapping
const TEMPLATE_ICONS = {
  server: Server,
  code: Code,
  workflow: Workflow,
  shield: Shield,
  users: Users
};

const DemandForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  // Debug mode for non-prod
  const isDebugMode = window.location.search.includes('debug=1');

  const [form, setForm] = useState({
    title: '',
    description: '',
    requestor: '',
    department: '',
    category: '',
    opex_flag: false,
    impact: 'medium',
    urgency: 'medium',
    strategic_alignment: 0,
    complexity: 0,
    business_value: 0,
    cost_estimate: '',
    business_outcomes: '',
    requirements: '',
    risks: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [debugPayload, setDebugPayload] = useState(null);
  
  // Template state
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [generatingTemplate, setGeneratingTemplate] = useState(null);
  const [templateInput, setTemplateInput] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    // TENANT ISOLATION: Include X-Tenant-ID header for sysadmin impersonation
    const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
    const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
    const activeTenantId = impersonatedTenantId || userTenantId;
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    if (activeTenantId) {
      headers['X-Tenant-ID'] = activeTenantId;
    }
    
    return headers;
  }, []);
  
  /**
   * Parse JSON:API error response and map to field errors
   * Handles errors[].source.pointer format
   */
  const parseJsonApiErrors = (errorResponse) => {
    const fieldErrors = {};
    
    if (errorResponse?.errors && Array.isArray(errorResponse.errors)) {
      errorResponse.errors.forEach(err => {
        // Extract field name from source.pointer (e.g., "/data/attributes/title")
        if (err.source?.pointer) {
          const pointer = err.source.pointer;
          const fieldMatch = pointer.match(/\/(?:data\/)?(?:attributes\/)?(\w+)$/);
          if (fieldMatch) {
            const fieldName = fieldMatch[1];
            fieldErrors[fieldName] = err.detail || err.title || 'Invalid value';
          }
        }
        // Handle simple detail message
        if (!Object.keys(fieldErrors).length && err.detail) {
          // Try to extract field from detail message
          const detailMatch = err.detail.match(/(\w+):\s*(.+)/);
          if (detailMatch) {
            fieldErrors[detailMatch[1].toLowerCase()] = detailMatch[2];
          }
        }
      });
    }
    
    // Handle Pydantic validation errors
    if (errorResponse?.detail && Array.isArray(errorResponse.detail)) {
      errorResponse.detail.forEach(err => {
        if (err.loc && err.loc.length > 0) {
          const fieldName = err.loc[err.loc.length - 1];
          fieldErrors[fieldName] = err.msg || 'Invalid value';
        }
      });
    }
    
    // Handle simple detail string
    if (typeof errorResponse?.detail === 'string') {
      // Try to extract field from message
      const match = errorResponse.detail.match(/(\w+)\s*(?:is|must|should|cannot)/i);
      if (match) {
        fieldErrors[match[1].toLowerCase()] = errorResponse.detail;
      }
    }
    
    return fieldErrors;
  };

  // Fetch templates on mount
  useEffect(() => {
    fetch(`${API_URL}/api/v2/demands/tpl/list`, {
      headers: getAuthHeaders()
    })
      .then(res => res.json())
      .then(data => setTemplates(data.data || []))
      .catch(err => console.error('Failed to load templates:', err));
  }, [getAuthHeaders]);

  // Load existing demand for editing
  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      fetch(`${API_URL}/api/v2/demands/${id}`, {
        headers: getAuthHeaders()
      })
        .then(res => res.json())
        .then(data => {
          if (data.data?.attributes) {
            const attrs = data.data.attributes;
            setForm({
              title: attrs.title || '',
              description: attrs.description || '',
              requestor: attrs.requestor || '',
              department: attrs.department || '',
              category: attrs.category || '',
              opex_flag: attrs.opex_flag || false,
              impact: attrs.impact || 'medium',
              urgency: attrs.urgency || 'medium',
              strategic_alignment: attrs.strategic_alignment || 0,
              complexity: attrs.complexity || 0,
              business_value: attrs.business_value || 0,
              cost_estimate: attrs.cost_estimate || '',
              business_outcomes: attrs.business_outcomes || '',
              requirements: attrs.requirements || '',
              risks: attrs.risks || ''
            });
          }
        })
        .catch(err => {
          console.error('Failed to load demand:', err);
          toast.error('Failed to load demand');
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEditing, getAuthHeaders]);

  // Generate from template
  const handleGenerateFromTemplate = async (templateId) => {
    if (!templateInput.trim()) {
      toast.error('Please describe what you need');
      return;
    }

    setGeneratingTemplate(templateId);
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/tpl/${templateId}/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_input: templateInput,
          department: form.department || ''
        })
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data;
        
        setForm(prev => ({
          ...prev,
          title: data.title || prev.title,
          description: data.description || prev.description,
          category: data.category || prev.category,
          impact: data.impact || prev.impact,
          urgency: data.urgency || prev.urgency,
          strategic_alignment: data.strategic_alignment || prev.strategic_alignment,
          complexity: data.complexity || prev.complexity,
          business_value: data.business_value || prev.business_value,
          business_outcomes: data.business_outcomes || prev.business_outcomes,
          requirements: data.requirements || prev.requirements,
          risks: data.risks || prev.risks
        }));

        toast.success(result.meta?.ai_enhanced ? 'AI generated demand content' : 'Template applied');
        setShowTemplates(false);
        setSelectedTemplate(null);
        setTemplateInput('');
      } else {
        toast.error('Failed to generate from template');
      }
    } catch (err) {
      console.error('Template generation failed:', err);
      toast.error('Failed to generate from template');
    } finally {
      setGeneratingTemplate(null);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.title || form.title.length < 3) {
      newErrors.title = 'Title is required (min 3 characters)';
    }
    if (!form.department) {
      newErrors.department = 'Department is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setSaving(true);
    setErrors({}); // Clear previous server errors

    try {
      const payload = {
        ...form,
        cost_estimate: form.cost_estimate ? parseFloat(form.cost_estimate) : null
      };
      
      // Debug mode: show payload
      if (isDebugMode) {
        setDebugPayload(payload);
      }

      const url = isEditing 
        ? `${API_URL}/api/v2/demands/${id}`
        : `${API_URL}/api/v2/demands`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(isEditing ? 'Demand updated' : 'Demand created');
        navigate(`/demands/${data.data?.id || id}`);
      } else {
        const errorResponse = await response.json();
        
        // Debug mode: log full error
        if (isDebugMode) {
          console.log('API Error Response:', errorResponse);
        }
        
        // Parse JSON:API errors and map to field errors
        const fieldErrors = parseJsonApiErrors(errorResponse);
        
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          toast.error('Please fix the highlighted fields');
        } else {
          // Fallback to generic error message
          toast.error(errorResponse.detail || errorResponse.message || 'Failed to save demand');
        }
      }
    } catch (err) {
      console.error('Failed to save demand:', err);
      toast.error('Failed to save demand. Please check your connection.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="demand-form">
      {/* Debug Panel - Only shown when ?debug=1 */}
      {isDebugMode && debugPayload && (
        <div className="mb-4 p-4 bg-gray-900 text-green-400 rounded-lg font-mono text-xs overflow-auto max-h-48">
          <div className="text-yellow-400 mb-2">Debug Mode - Request Payload:</div>
          <pre>{JSON.stringify(debugPayload, null, 2)}</pre>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {isEditing ? 'Edit Demand' : 'New Demand'}
          </h1>
          <p className="text-gray-500">
            {isEditing ? 'Update demand details' : 'Submit a new business demand for review'}
          </p>
          {isDebugMode && (
            <Badge variant="outline" className="mt-1 text-orange-600 border-orange-300">Debug Mode</Badge>
          )}
        </div>
        {!isEditing && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowTemplates(true)}
            className="flex items-center gap-2"
            data-testid="start-from-template"
          >
            <Sparkles className="h-4 w-4 text-purple-500" />
            Start from Template
          </Button>
        )}
      </div>

      {/* Template Selector Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="template-modal">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    AI-Powered Templates
                  </CardTitle>
                  <CardDescription>Choose a template and describe your need</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => {
                  setShowTemplates(false);
                  setSelectedTemplate(null);
                  setTemplateInput('');
                }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 overflow-y-auto max-h-[60vh]">
              {!selectedTemplate ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map((template) => {
                    const IconComponent = TEMPLATE_ICONS[template.icon] || Server;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setSelectedTemplate(template)}
                        className="p-4 border rounded-lg hover:border-purple-300 hover:bg-purple-50 text-left transition-colors"
                        data-testid={`template-${template.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <IconComponent className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">{template.name}</p>
                            <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      {(() => {
                        const IconComponent = TEMPLATE_ICONS[selectedTemplate.icon] || Server;
                        return <IconComponent className="h-5 w-5 text-purple-600" />;
                      })()}
                    </div>
                    <div>
                      <p className="font-medium">{selectedTemplate.name}</p>
                      <p className="text-sm text-gray-500">{selectedTemplate.description}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="ml-auto"
                      onClick={() => setSelectedTemplate(null)}
                    >
                      Change
                    </Button>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Describe what you need
                    </label>
                    <textarea
                      value={templateInput}
                      onChange={(e) => setTemplateInput(e.target.value)}
                      placeholder={`E.g., "We need a new Kubernetes cluster for the data analytics team with GPU support"`}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-h-[100px]"
                      data-testid="template-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      AI will generate title, description, and requirements based on your input
                    </p>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSelectedTemplate(null)}
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => handleGenerateFromTemplate(selectedTemplate.id)}
                      disabled={generatingTemplate !== null || !templateInput.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="generate-template"
                    >
                      {generatingTemplate ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Demand
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
            <CardDescription>Core details about the demand</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md ${errors.title ? 'border-red-500' : ''}`}
                placeholder="Brief title describing the demand"
                data-testid="input-title"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full px-3 py-2 border rounded-md h-24"
                placeholder="Detailed description of the demand, including business justification..."
                data-testid="input-description"
              />
            </div>

            {/* Requestor */}
            <div>
              <label className="block text-sm font-medium mb-1">Requestor</label>
              <input
                type="text"
                value={form.requestor}
                onChange={(e) => handleChange('requestor', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Name or email of requestor"
                data-testid="input-requestor"
              />
            </div>

            {/* Department & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Department <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md ${errors.department ? 'border-red-500' : ''}`}
                  placeholder="e.g., Finance, IT, Operations"
                  data-testid="input-department"
                />
                {errors.department && (
                  <p className="text-red-500 text-sm mt-1">{errors.department}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="e.g., Infrastructure, Process, Product"
                  data-testid="input-category"
                />
              </div>
            </div>

            {/* OPEX Flag */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="opex_flag"
                checked={form.opex_flag}
                onChange={(e) => handleChange('opex_flag', e.target.checked)}
                className="h-4 w-4 rounded"
                data-testid="input-opex"
              />
              <label htmlFor="opex_flag" className="text-sm">
                This is an OPEX (operational expense) demand
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Assessment Criteria */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Assessment Criteria</CardTitle>
            <CardDescription>Factors used for prioritization scoring</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Impact & Urgency */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Impact</label>
                <select
                  value={form.impact}
                  onChange={(e) => handleChange('impact', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                  data-testid="select-impact"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Urgency</label>
                <select
                  value={form.urgency}
                  onChange={(e) => handleChange('urgency', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-white"
                  data-testid="select-urgency"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            {/* Scoring Factors */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Strategic Alignment (0-5)
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={form.strategic_alignment}
                  onChange={(e) => handleChange('strategic_alignment', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="input-strategic"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Complexity (0-5)
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={form.complexity}
                  onChange={(e) => handleChange('complexity', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="input-complexity"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Business Value (0-5)
                </label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={form.business_value}
                  onChange={(e) => handleChange('business_value', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="input-business-value"
                />
              </div>
            </div>

            {/* Cost Estimate */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Cost Estimate (optional)
              </label>
              <input
                type="number"
                value={form.cost_estimate}
                onChange={(e) => handleChange('cost_estimate', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
                placeholder="Estimated cost in currency units"
                data-testid="input-cost"
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="submit-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : (isEditing ? 'Update Demand' : 'Submit Demand')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DemandForm;
